import React, { useState } from 'react';
import { useShop } from '../../context/ShopContext';
import { formatINR } from '../../data/products';
import { 
  ShoppingBag, 
  Truck, 
  CheckCircle2, 
  Clock, 
  XCircle, 
  Search, 
  Phone, 
  MapPin, 
  MessageSquare, 
  Printer,
  Plus,
  ShieldCheck,
  PackageCheck,
  X
} from 'lucide-react';

export default function AdminOrders() {
  const { 
    orders, 
    updateOrderStatus, 
    addManualOrder, 
    setInvoiceModalOrder, 
    products, 
    addToast 
  } = useShop();

  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [isManualModalOpen, setIsManualModalOpen] = useState(false);

  // Manual Order Form State
  const [manualForm, setManualForm] = useState({
    customerName: '',
    phone: '',
    email: '',
    address: 'Jaipur Showroom Counter Pickup',
    productId: products[0]?.id || '',
    customItemName: '',
    price: products[0]?.price || 50000,
    qty: 1,
    paymentMethod: 'UPI (Showroom QR)'
  });

  const filteredOrders = orders.filter((o) => {
    const matchesStatus = filterStatus === 'all' || o.status === filterStatus;
    const matchesSearch = 
      o.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      o.customer?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      o.customer?.phone?.includes(searchTerm);
    return matchesStatus && matchesSearch;
  });

  const handlePrintInvoice = (order) => {
    setInvoiceModalOrder(order);
  };

  const handleWhatsAppNotify = (order) => {
    const text = encodeURIComponent(`Namaste ${order.customer?.name}, your M.L. Jewellers & Kitchenware order #${order.id} status is: ${order.status}. Total: ${formatINR(order.total)}. For priority concierge assistance, call us.`);
    window.open(`https://api.whatsapp.com/send?phone=91${order.customer?.phone}&text=${text}`, '_blank');
  };

  const handleCreateManualOrder = (e) => {
    e.preventDefault();
    const selectedProd = products.find(p => p.id === manualForm.productId);
    const itemName = manualForm.customItemName.trim() || selectedProd?.name || 'Handcrafted Heritage Jewellery';
    const itemPrice = Number(manualForm.price) || selectedProd?.price || 50000;
    const itemQty = Number(manualForm.qty) || 1;
    const total = itemPrice * itemQty;

    const newOrder = addManualOrder({
      customer: {
        name: manualForm.customerName.trim(),
        phone: manualForm.phone.trim(),
        email: manualForm.email.trim() || 'walkin.customer@mljewellers.com',
        address: manualForm.address.trim()
      },
      items: [
        {
          name: itemName,
          qty: itemQty,
          price: itemPrice,
          metal: selectedProd?.metal || 'BIS 916 Hallmarked Gold',
          hsn: selectedProd?.category === 'kitchenware' ? '7323' : '7113'
        }
      ],
      paymentMethod: manualForm.paymentMethod,
      total: total,
      courier: 'Hand Delivered at Showroom Counter',
      trackingNumber: `CTR-${Math.floor(100000 + Math.random() * 900000)}`
    });

    setIsManualModalOpen(false);
    setManualForm({
      customerName: '',
      phone: '',
      email: '',
      address: 'Jaipur Showroom Counter Pickup',
      productId: products[0]?.id || '',
      customItemName: '',
      price: products[0]?.price || 50000,
      qty: 1,
      paymentMethod: 'UPI (Showroom QR)'
    });
  };

  return (
    <div>
      {/* Header */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '1rem',
        marginBottom: '1.5rem'
      }}>
        <div>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 800, color: '#fff' }}>
            Customer <span className="text-gold-gradient">Vault Orders</span>
          </h2>
          <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>
            Track tamper-proof shipments, generate GST tax invoices, and create offline walk-in orders.
          </p>
        </div>

        <div style={{ display: 'flex', gap: '0.8rem', alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Search */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            background: '#13131e',
            border: '1px solid var(--border-subtle)',
            borderRadius: 'var(--radius-sm)',
            padding: '0.45rem 0.8rem',
            width: '240px'
          }}>
            <Search size={15} color="var(--gold-400)" style={{ marginRight: '0.5rem' }} />
            <input
              type="text"
              placeholder="Search Order ID, name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                background: 'transparent',
                border: 'none',
                outline: 'none',
                color: '#fff',
                fontSize: '0.84rem',
                width: '100%'
              }}
            />
          </div>

          <button
            className="btn btn-gold btn-sm"
            onClick={() => setIsManualModalOpen(true)}
            style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', fontSize: '0.8rem' }}
          >
            <Plus size={15} />
            <span>Create Walk-in Order</span>
          </button>
        </div>
      </div>

      {/* Status Tabs */}
      <div style={{
        display: 'flex',
        gap: '0.6rem',
        overflowX: 'auto',
        paddingBottom: '0.8rem',
        marginBottom: '1.5rem'
      }}>
        {[
          { id: 'all', label: 'All Orders', count: orders.length },
          { id: 'Processing', label: 'Processing', count: orders.filter(o => o.status === 'Processing').length },
          { id: 'In Vault Transit', label: 'In Transit', count: orders.filter(o => o.status === 'In Vault Transit').length },
          { id: 'Delivered', label: 'Delivered', count: orders.filter(o => o.status === 'Delivered').length },
          { id: 'Cancelled', label: 'Cancelled', count: orders.filter(o => o.status === 'Cancelled').length },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setFilterStatus(tab.id)}
            style={{
              background: filterStatus === tab.id ? 'var(--gold-gradient)' : '#13131e',
              color: filterStatus === tab.id ? '#0a0a0f' : 'var(--text-secondary)',
              border: filterStatus === tab.id ? '1px solid var(--gold-300)' : '1px solid var(--border-subtle)',
              padding: '0.45rem 1rem',
              borderRadius: 'var(--radius-full)',
              fontSize: '0.8rem',
              fontWeight: 700,
              cursor: 'pointer',
              whiteSpace: 'nowrap'
            }}
          >
            {tab.label} ({tab.count})
          </button>
        ))}
      </div>

      {/* Orders List */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
        {filteredOrders.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '4rem 1rem',
            background: '#13131e',
            borderRadius: 'var(--radius-md)',
            color: 'var(--text-muted)'
          }}>
            <ShoppingBag size={40} color="var(--gold-400)" style={{ margin: '0 auto 0.8rem', opacity: 0.6 }} />
            <h4 style={{ color: '#fff', fontSize: '1.1rem' }}>No orders found</h4>
            <p style={{ fontSize: '0.85rem' }}>No orders matching current filter or search criteria.</p>
          </div>
        ) : (
          filteredOrders.map((ord) => (
            <div
              key={ord.id}
              style={{
                background: '#13131e',
                border: '1px solid var(--border-subtle)',
                borderRadius: 'var(--radius-md)',
                padding: '1.5rem',
                display: 'flex',
                flexDirection: 'column',
                gap: '1.2rem'
              }}
            >
              {/* Header row */}
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '0.8rem',
                borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
                paddingBottom: '1rem'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
                  <span style={{ fontSize: '1.15rem', fontWeight: 800, color: 'var(--gold-400)', fontFamily: 'var(--font-serif)' }}>
                    #{ord.id}
                  </span>
                  <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                    {ord.date}
                  </span>
                  {ord.courier && (
                    <span style={{
                      fontSize: '0.7rem',
                      background: 'rgba(52, 211, 153, 0.1)',
                      color: '#34d399',
                      border: '1px solid rgba(52, 211, 153, 0.3)',
                      padding: '0.1rem 0.5rem',
                      borderRadius: '4px'
                    }}>
                      {ord.courier}
                    </span>
                  )}
                </div>

                {/* Status Dropdown */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                  <label style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Status:</label>
                  <select
                    value={ord.status}
                    onChange={(e) => updateOrderStatus(ord.id, e.target.value)}
                    style={{
                      background: '#0e0e16',
                      border: '1px solid var(--border-gold)',
                      color: ord.status === 'Delivered' ? '#34d399' : ord.status === 'Cancelled' ? '#f87171' : 'var(--gold-400)',
                      fontWeight: 700,
                      padding: '0.35rem 0.75rem',
                      borderRadius: 'var(--radius-sm)',
                      fontSize: '0.8rem',
                      outline: 'none',
                      cursor: 'pointer'
                    }}
                  >
                    <option value="Processing">Processing</option>
                    <option value="In Vault Transit">In Vault Transit</option>
                    <option value="Delivered">Delivered</option>
                    <option value="Cancelled">Cancelled</option>
                  </select>
                </div>
              </div>

              {/* Order Info Grid */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
                gap: '1.5rem'
              }}>
                {/* Customer Details */}
                <div>
                  <div style={{ fontSize: '0.74rem', textTransform: 'uppercase', color: 'var(--text-muted)', fontWeight: 700, marginBottom: '0.4rem' }}>
                    Patron & Vault Destination
                  </div>
                  <div style={{ fontWeight: 700, color: '#fff', fontSize: '0.92rem' }}>
                    {ord.customer?.name}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: 'var(--text-secondary)', fontSize: '0.82rem', margin: '0.2rem 0' }}>
                    <Phone size={13} color="var(--gold-400)" />
                    <span>+91 {ord.customer?.phone}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.4rem', color: 'var(--text-muted)', fontSize: '0.8rem' }}>
                    <MapPin size={13} color="var(--gold-400)" style={{ flexShrink: 0, marginTop: '2px' }} />
                    <span>{ord.customer?.address}</span>
                  </div>
                </div>

                {/* Items */}
                <div>
                  <div style={{ fontSize: '0.74rem', textTransform: 'uppercase', color: 'var(--text-muted)', fontWeight: 700, marginBottom: '0.4rem' }}>
                    Purchased Articles ({ord.items?.length || 0})
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                    {ord.items?.map((item, i) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.84rem' }}>
                        <span style={{ color: '#fff' }}>
                          {item.name} <strong style={{ color: 'var(--gold-400)' }}>× {item.qty}</strong>
                        </span>
                        <span style={{ color: 'var(--text-secondary)', fontFamily: 'var(--font-serif)', fontWeight: 600 }}>
                          {formatINR(item.price * item.qty)}
                        </span>
                      </div>
                    ))}
                  </div>

                  <div style={{
                    marginTop: '0.6rem',
                    paddingTop: '0.5rem',
                    borderTop: '1px solid rgba(255, 255, 255, 0.08)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'baseline'
                  }}>
                    <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>Payment ({ord.paymentMethod})</span>
                    <span style={{ fontSize: '1.1rem', fontWeight: 800, color: 'var(--gold-400)', fontFamily: 'var(--font-serif)' }}>
                      {formatINR(ord.total)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div style={{
                display: 'flex',
                gap: '0.8rem',
                flexWrap: 'wrap',
                paddingTop: '0.5rem',
                borderTop: '1px solid rgba(255, 255, 255, 0.04)'
              }}>
                <button
                  className="btn btn-gold btn-sm"
                  onClick={() => handlePrintInvoice(ord)}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.78rem' }}
                >
                  <Printer size={14} /> Print GST Tax Invoice & BIS Hallmark
                </button>
                <button
                  className="btn btn-glass btn-sm"
                  onClick={() => handleWhatsAppNotify(ord)}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.78rem', color: '#25D366' }}
                >
                  <MessageSquare size={14} /> WhatsApp Dispatch Update
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Manual Walk-in Order Modal */}
      {isManualModalOpen && (
        <div style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.85)',
          backdropFilter: 'blur(8px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '1.5rem'
        }}>
          <div style={{
            width: '100%',
            maxWidth: '560px',
            backgroundColor: '#13131e',
            border: '1px solid var(--border-gold)',
            borderRadius: 'var(--radius-md)',
            padding: '2rem',
            position: 'relative'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <div>
                <h3 style={{ fontSize: '1.3rem', fontWeight: 800, color: '#fff' }}>
                  Create Walk-in / Offline Order
                </h3>
                <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                  Record counter sale, generate immediate GST invoice, and update stock.
                </p>
              </div>
              <button
                onClick={() => setIsManualModalOpen(false)}
                style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer' }}
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleCreateManualOrder} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: 'var(--gold-400)', marginBottom: '0.3rem' }}>
                  Customer Full Name *
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Seth Lalit Kishore"
                  value={manualForm.customerName}
                  onChange={(e) => setManualForm({ ...manualForm, customerName: e.target.value })}
                  className="input-luxury"
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: 'var(--gold-400)', marginBottom: '0.3rem' }}>
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    required
                    placeholder="98290 12345"
                    value={manualForm.phone}
                    onChange={(e) => setManualForm({ ...manualForm, phone: e.target.value })}
                    className="input-luxury"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: 'var(--gold-400)', marginBottom: '0.3rem' }}>
                    Payment Mode
                  </label>
                  <select
                    value={manualForm.paymentMethod}
                    onChange={(e) => setManualForm({ ...manualForm, paymentMethod: e.target.value })}
                    className="input-luxury"
                  >
                    <option value="UPI (Showroom QR)">UPI (Showroom QR)</option>
                    <option value="Credit/Debit Card (POS)">Credit/Debit Card (POS)</option>
                    <option value="Cash Counter">Cash Counter</option>
                    <option value="Bank NEFT / RTGS">Bank NEFT / RTGS</option>
                  </select>
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: 'var(--gold-400)', marginBottom: '0.3rem' }}>
                  Select Catalog Article or Custom Item
                </label>
                <select
                  value={manualForm.productId}
                  onChange={(e) => {
                    const sel = products.find(p => p.id === e.target.value);
                    setManualForm({
                      ...manualForm,
                      productId: e.target.value,
                      price: sel ? sel.price : manualForm.price
                    });
                  }}
                  className="input-luxury"
                  style={{ marginBottom: '0.5rem' }}
                >
                  {products.map(p => (
                    <option key={p.id} value={p.id}>{p.name} - {formatINR(p.price)}</option>
                  ))}
                </select>
                <input
                  type="text"
                  placeholder="Or write custom description (optional)"
                  value={manualForm.customItemName}
                  onChange={(e) => setManualForm({ ...manualForm, customItemName: e.target.value })}
                  className="input-luxury"
                  style={{ fontSize: '0.8rem' }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: 'var(--gold-400)', marginBottom: '0.3rem' }}>
                    Final Agreed Amount (₹) *
                  </label>
                  <input
                    type="number"
                    required
                    value={manualForm.price}
                    onChange={(e) => setManualForm({ ...manualForm, price: e.target.value })}
                    className="input-luxury"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: 'var(--gold-400)', marginBottom: '0.3rem' }}>
                    Quantity
                  </label>
                  <input
                    type="number"
                    min="1"
                    required
                    value={manualForm.qty}
                    onChange={(e) => setManualForm({ ...manualForm, qty: e.target.value })}
                    className="input-luxury"
                  />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '0.8rem', marginTop: '1rem' }}>
                <button
                  type="button"
                  onClick={() => setIsManualModalOpen(false)}
                  className="btn btn-glass"
                  style={{ flex: 1 }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-gold"
                  style={{ flex: 1 }}
                >
                  Generate Order & Invoice
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
